import React from 'react'
import cx from 'classnames'

import { Tabs } from 'components/shared/Tabs/Tabs'
import { TabContent } from './common/TabContent/TabContent'
import { RequireRole } from 'components/auth/features'
import { useTabScrollIntoView } from 'hooks'
import { ALIGNMENT_NAMES } from './model'

import styles from './TabsBar.module.scss'

type TabDescriptor = {
  id: string
  label?: React.ReactNode
  sequenceNumber?: number
  isDisabled?: boolean
  isFinished?: boolean
  isInvalid?: boolean
  adminOnly?: boolean
  caution?: number
}

type TabsBarProps = {
  activeTab?: string
  alignment?: string
  className?: string
  tabClassName?: string
  tabs?: TabDescriptor[]
  onClick?: (id: string) => void
  isScrollable?: boolean
  isCentered?: boolean
  isOutlined?: boolean
}

export const TabsBar = ({
  activeTab,
  alignment,
  className,
  tabClassName,
  tabs = [],
  onClick,
  isScrollable,
  isCentered,
  isOutlined,
}: TabsBarProps) => {
  const ref = useTabScrollIntoView(activeTab)

  return (
    <Tabs
      className={cx(styles.tabsBar, styles.alignment, className, {
        [styles['top-left']]: alignment === ALIGNMENT_NAMES.TOP_LEFT,
        [styles.centered]: isCentered,
        [styles.outlined]: isOutlined,
      })}
      isScrollable={isScrollable}
    >
      {tabs.map(
        ({
          id,
          label,
          sequenceNumber,
          isDisabled,
          isFinished,
          isInvalid,
          adminOnly,
          caution,
        }) => {
          const isActiveTab = activeTab === id

          const tabComponent = (
            <Tabs.Tab
              {...(!isDisabled && { onClick: () => onClick?.(id) })}
              className={cx(styles.tab, tabClassName, {
                [styles.active]: isActiveTab,
                [styles.invalid]: isInvalid,
              })}
              key={id}
              ref={
                isActiveTab ? (ref as React.RefObject<HTMLButtonElement>) : null
              }
              {...{ id, isDisabled }}
            >
              <TabContent
                className={styles.content}
                isActive={isActiveTab}
                {...{
                  label,
                  sequenceNumber,
                  isDisabled,
                  isFinished,
                  isInvalid,
                  caution,
                }}
              />
            </Tabs.Tab>
          )

          if (adminOnly) {
            return (
              <RequireRole key={id} role="accountAdmin">
                {tabComponent}
              </RequireRole>
            )
          }

          return tabComponent
        },
      )}
    </Tabs>
  )
}
